import * as React from 'react';
import { HcpGroup } from '@ixicare/resources';
import type { HcpGroupEntryProps } from '@ixicare/resources';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Table, IconButton, Modal } from '@ixicare/ui';
import { SquarePen } from 'lucide-react';
import { observer } from 'mobx-react';
import { t } from '~/i18n';
import { useStores } from '~store/utils';
import { useHcpGroups } from '~/hooks/useHcpGroups';
import { useZonePresets } from '~modules/zone-presets/useZonePresets';
import GeofenceCreatorContainer from '~modules/geofencing/container/GeofenceCreatorContainer';
import type { GeofenceSelection } from '~modules/zone-presets/useZonePresetSelection';
import modalStyles from '~modules/geofencing/container/geofenceEditorModal.module.css';

export const ZoneAssignmentTab: React.FC = observer(() => {
  const queryClient = useQueryClient();
  const { notifications, sites } = useStores();
  const site = sites.selectedSite;
  const [editing, setEditing] = React.useState<HcpGroupEntryProps | null>(null);

  const { data: groups } = useHcpGroups(site?.uuid);
  // Only to name the preset a group follows; the editor loads its own copy.
  const { data: presets } = useZonePresets(site?.id);

  const save = useMutation({
    mutationFn: ({ groupId, selection }: { groupId: string; selection: GeofenceSelection }) =>
      // One key or the other, never both - the API refuses the pair, because they say different
      // things about where the same alarms go.
      HcpGroup.updateHcpGroup(
        groupId,
        selection.zonePresetId ? { zonePresetId: selection.zonePresetId } : { zoneIds: selection.zoneIds }
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['hcpGroups'] });
      notifications.addUserActionNotification({
        notificationType: 'success',
        message: t('hcpGroups.zone.assign.success'),
      });
    },
    onError: () => {
      notifications.addUserActionNotification({
        notificationType: 'error',
        message: t('hcpGroups.zone.remove.error'),
      });
    },
  });

  const describe = (group: HcpGroupEntryProps) => {
    const followed = presets?.find((preset) => preset.id === group.zonePresetId);
    if (followed) return t('geofence.preset.following', { name: followed.name });
    if (!group.zones?.length) return t('hcpGroups.no.zones');
    return group.zones.map((zone) => zone.name).join(', ');
  };

  return (
    <>
      <Table>
        <Table.Head>
          <Table.Row>
            <Table.HeadCol>{t('hcpGroups.table.groupName')}</Table.HeadCol>
            <Table.HeadCol>{t('hcpGroups.assigned.zones')}</Table.HeadCol>
            {/* Action column header intentionally empty - icons carry `title` tooltips. */}
            <Table.HeadCol>{''}</Table.HeadCol>
          </Table.Row>
        </Table.Head>
        <Table.Body>
          {Array.isArray(groups) &&
            groups.map((group) => (
              <Table.Row key={group.id}>
                <Table.Col>{group.hcpGroupName}</Table.Col>
                <Table.Col>{describe(group)}</Table.Col>
                <Table.Col>
                  <span title={t('residents.table.edit')}>
                    <IconButton lucide={SquarePen} onClick={() => setEditing(group)} />
                  </span>
                </Table.Col>
              </Table.Row>
            ))}
        </Table.Body>
      </Table>

      {editing && site && (
        <Modal onClose={() => setEditing(null)} className={modalStyles.wide}>
          <h3 className="text-center mb-30">{t('hcpGroups.zone.edit.title', { name: editing.hcpGroupName })}</h3>
          <GeofenceCreatorContainer
            siteUuid={site.uuid}
            siteId={site.id}
            subject="group"
            selectedZones={(editing.zones ?? []).map((zone) => zone.id)}
            zonePresetId={editing.zonePresetId}
            onSave={(selection) => {
              save.mutate({ groupId: editing.id, selection });
              setEditing(null);
            }}
            onCancel={() => setEditing(null)}
          />
        </Modal>
      )}
    </>
  );
});
